import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Coins } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [credits, setCredits] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCredits(data.credits_added);
          setState("paid");
          await checkAuth();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status) || attempts.current > 12) {
          setState("failed");
          return;
        }
      } catch {
        if (attempts.current > 12) {
          setState("failed");
          return;
        }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [checkAuth]);

  return (
    <div className="h-screen flex items-center justify-center bg-[#F4F4F5] text-foreground px-6">
      <div className="bg-white border border-border rounded-sm p-10 max-w-md w-full text-center flex flex-col items-center gap-4" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#002FA7]" />
            <h1 className="font-display text-2xl tracking-tight font-semibold">Confirmando tu pago…</h1>
            <p className="text-neutral-500 text-sm">Un momento, estamos añadiendo tus créditos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-green-600" />
            <h1 className="font-display text-3xl tracking-tight font-semibold">¡Pago completado!</h1>
            <div className="flex items-center gap-2 text-[#CA8A04] text-lg font-semibold">
              <Coins className="w-5 h-5" /> +{credits} créditos añadidos
            </div>
            <Button onClick={() => navigate("/")} className="rounded-sm bg-[#002FA7] hover:bg-[#00248a] mt-2" data-testid="go-editor-btn">
              Volver al editor
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-red-500" />
            <h1 className="font-display text-2xl tracking-tight font-semibold">No pudimos confirmar el pago</h1>
            <p className="text-neutral-500 text-sm">Si se te ha cobrado, tus créditos aparecerán en breve.</p>
            <Button onClick={() => navigate("/credits")} className="rounded-sm mt-2" data-testid="back-credits-btn">
              Ir a créditos
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
